import { ParseMode } from 'mathlive'
import { IMathLiveKeyboardOptions, TMathLiveInputOptions } from '../types'

export const initialMathLiveInputMode: ParseMode = 'text'
export const modeChangeSwitchObj: { [key in ParseMode]: ParseMode } = {
  math: 'text',
  text: 'math',
  latex: 'math',
}

export const initialMathLiveInputOptions: TMathLiveInputOptions = {
  mathVirtualKeyboardPolicy: 'manual',
  mode: initialMathLiveInputMode,
  defaultMode: initialMathLiveInputMode,
  placeholder: undefined,
  autofocus: true,
  mathModeSpace: '\\:', // add LaTex medium space on pressing the space-bar in math mode
}

export const initialMathLiveKeyboardOptions: IMathLiveKeyboardOptions = {
  layouts: ['numeric', 'symbols', 'greek'],
}

/**
 * Ensures that a MathLive LaTeX string is wrapped with the `\displaylines{}` operator.
 *
 * This function takes a string input (typically returned from MathLive, enclosed in dollar signs),
 * checks whether it is already wrapped in a `\displaylines{}` block, and wraps the content if not.
 *
 * - If the input is wrapped with `$...$` and does not start with `\displaylines`, the inner content
 *   is wrapped inside `\displaylines{...}`.
 * - If the input is not properly wrapped with `$...$` but does not include `\displaylines`, the
 *   entire trimmed value is wrapped.
 *
 * @param value - The LaTeX string input to wrap.
 * @returns A LaTeX string properly wrapped in `$\\displaylines{...}$` if needed.
 */
export function wrapMathLiveInputWithDisplayLinesOperator(value: string) {
  // Check for wrapping in $...$
  const match = value.match(/^\$(.*)\$$/)

  if (match) {
    const inner = match[1].trim()

    if (!inner.startsWith('\\displaylines')) {
      value = `$ \\displaylines{${inner}} $`
    }
  } else {
    // In case value is not properly wrapped in $...$, still try to handle it
    if (!value.includes('\\displaylines')) {
      value = `$ \\displaylines{${value.trim()}} $`
    }
  }

  return value
}

/**
 * Wraps a plain string in LaTeX inline math syntax using \text{},
 * unless it's already properly wrapped.
 *
 * Example:
 *   wrapInLatexText("Ok, lets learn") => "$ \\text{Ok, lets learn} $"
 *   wrapInLatexText("$ \\text{Already wrapped} $") => "$ \\text{Already wrapped} $"
 *
 * @param {string} str - The input string to wrap.
 * @returns {string} - The LaTeX-wrapped string.
 */
export function wrapInLatexText(str: string): string {
  const trimmed = str.trim()

  const alreadyWrapped = /^\$\s*\\text\{[^}]*\}\s*\$$/.test(trimmed)

  if (alreadyWrapped) {
    return trimmed
  }

  // Optional: Escape special LaTeX characters inside the string
  const escaped = str.replace(/([{}])/g, '\\$1')

  return `$ \\text{${escaped}} $`
}

export const mathLiveInputHelperText = `Use Enter to submit, Shift+Enter for new line`

export function checkIsOnlyEmptyDisplayLines(input: string): boolean {
  const displaylinesRegex = /^\$\s*\\displaylines\s*\{([^}]*)\}\s*\$$/

  const match = input.match(displaylinesRegex)
  if (!match) return false

  const content = match[1].trim()

  // Remove all `\\` (escaped backslashes), trim remaining
  const contentWithoutDoubleSlashes = content
    .replace(/\\\\/g, '') // remove all instances of `\\`
    .trim()

  return contentWithoutDoubleSlashes === ''
}

const DISPLAY_LINES_OPERATOR = '\\displaylines'

/**
 * Extracts the inner content of every `\displaylines{...}` block in a string,
 * using brace matching (the body can carry nested environments like
 * `\begin{pmatrix}`). Returns [start, end, inner] tuples; end is exclusive.
 */
const findDisplayLinesBlocks = (
  value: string,
): Array<{ start: number; end: number; inner: string }> => {
  const blocks: Array<{ start: number; end: number; inner: string }> = []
  let searchFrom = 0

  for (;;) {
    const start = value.indexOf(DISPLAY_LINES_OPERATOR, searchFrom)

    if (start === -1) {
      break
    }

    const braceStart = start + DISPLAY_LINES_OPERATOR.length

    if (value[braceStart] !== '{') {
      searchFrom = braceStart
      continue
    }

    let depth = 0
    let i = braceStart

    for (; i < value.length; i++) {
      if (value[i] === '{') {
        depth += 1
      } else if (value[i] === '}') {
        depth -= 1

        if (depth === 0) {
          break
        }
      }
    }

    if (depth !== 0) {
      // Unbalanced braces (a truncated stored value) - stop matching here;
      // callers strip the leftover bare macro below.
      break
    }

    blocks.push({
      start,
      end: i + 1,
      inner: value.slice(braceStart + 1, i),
    })
    searchFrom = i + 1
  }

  return blocks
}

/**
 * Rewrites `\displaylines{...}` blocks as `\begin{aligned}...\end{aligned}`.
 * KaTeX (and the Mathpix renderer built on it) has no `\displaylines` macro,
 * so stored MathLive working renders as the raw macro string (Mike's
 * 9 Sep marking-results defect). MathLive itself renders `\displaylines`
 * natively, so this is only applied on display/seed paths - never to values
 * handed back to the editor.
 */
export function normalizeDisplayLinesForRender(value: string) {
  if (!value.includes(DISPLAY_LINES_OPERATOR)) {
    return value
  }

  let result = ''
  let cursor = 0

  for (const block of findDisplayLinesBlocks(value)) {
    result += value.slice(cursor, block.start)
    result += `\\begin{aligned}${block.inner}\\end{aligned}`
    cursor = block.end
  }

  result += value.slice(cursor)

  // Truncated stored values can leave a bare unmatched macro behind - drop
  // the keyword so the inner math still renders.
  return result.replace(/\\displaylines/g, '')
}

/**
 * Rewrites a `\displaylines`-wrapped value as one math segment per working
 * line, separated by newlines: `$ \displaylines{A \\\\ B} $` -> `$A$\n$B$`.
 * Used when the working is sent to the marking model: as one blob the model
 * annotates the whole macro as a single "line" (truncating it), so Axi's
 * line-by-line marking and the Ask-Axi seed lose the actual content.
 */
export function displayLinesToSeparateLines(value: string) {
  if (!value.includes(DISPLAY_LINES_OPERATOR)) {
    return value
  }

  const blocks = findDisplayLinesBlocks(value)

  if (blocks.length === 0) {
    return value
  }

  // Single block covering the whole (possibly $...$-wrapped) value: emit one
  // $...$ math segment per `\\`-separated line.
  const single = blocks.length === 1 ? blocks[0] : null

  if (single) {
    const before = value.slice(0, single.start).replace(/[$\s]/g, '')
    const after = value.slice(single.end).replace(/[$\s]/g, '')

    if (!before && !after) {
      return single.inner
        .split('\\\\')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => `$${line}$`)
        .join('\n')
    }
  }

  // Fallback: unwrap each block, keeping the `\\` separators as newlines
  let result = ''
  let cursor = 0

  for (const block of blocks) {
    result += value.slice(cursor, block.start)
    result += block.inner.split('\\\\').join('\n')
    cursor = block.end
  }

  result += value.slice(cursor)

  return result.replace(/\\displaylines/g, '')
}
